import {
  Dispatch,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CheckCheck, X } from 'lucide-react';
import EditField from 'src/shared/components/entities/components/EditField';
import { cn } from 'src/shared/lib/utils';
import { ActualsUpdateType } from 'src/features/messages/types/message.types';
import WithTooltip from 'src/shared/components/utils/WithTooltip';

type PrimitiveEntityValueProps = {
  path: string[];
  verified: boolean;
  values: string[] | null;
  highlightPath: string[];
  setHighlightPath: Dispatch<SetStateAction<string[]>>;
  onEditSave?: (values: string[], path: string[], action: ActualsUpdateType) => void;
};

export default function PrimitiveEntityValue({
  path,
  values = [],
  highlightPath,
  verified,
  setHighlightPath,
  onEditSave,
}: PrimitiveEntityValueProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [editValues, setEditValues] = useState<string[]>(values || []);
  const [focussedIndex, setFocussedIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const isInEditMode = highlightPath.join('-') === path.join('-');

  const editFieldRef = useRef<HTMLDivElement>(null);

  const markLastItemsInRows = useCallback(() => {
    const valueItems = containerRef.current?.querySelectorAll('.value-item');
    if (!valueItems) return;

    for (const item of valueItems) {
      item.classList.remove('flex-row-last');
    }

    let currentRowTop = 0;
    let lastItemInCurrentRow = null;
    for (const item of valueItems) {
      const htmlItem = item as HTMLElement;
      const itemTop = htmlItem.offsetTop;

      if (Math.abs(currentRowTop - itemTop) > 10) {
        if (lastItemInCurrentRow) {
          lastItemInCurrentRow.classList.add('flex-row-last');
        }
        currentRowTop = itemTop;
        lastItemInCurrentRow = item;
      } else {
        lastItemInCurrentRow = item;
      }
    }

    if (lastItemInCurrentRow && valueItems.length > 1) {
      lastItemInCurrentRow.classList.add('flex-row-last');
    }
  }, []);

  const handleEditFieldMutation = useCallback(
    (mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const element = mutation.target as HTMLElement;
          if (element.classList.contains('block') && !element.classList.contains('hidden')) {
            markLastItemsInRows();
          }
        }
      });
    },
    [markLastItemsInRows]
  );

  // Observers
  useEffect(() => {
    // recalculate last row items in the flex box when container resizes
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      markLastItemsInRows();
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [markLastItemsInRows]);

  useEffect(() => {
    // detect when "Add value" EditField goes from hidden state to visible state (block)
    if (editFieldRef.current) {
      const observer = new MutationObserver(handleEditFieldMutation);

      observer.observe(editFieldRef.current, { attributes: true });
      return () => observer.disconnect();
    }
  }, [handleEditFieldMutation]);

  // keep in sync with ground truth
  useEffect(() => {
    setEditValues(values || []);

    // calculate the last row items in the flex-box, to prevent overflow when delete button expands
    markLastItemsInRows();
  }, [values, markLastItemsInRows]);

  // reset focussed index when not in edit mode
  useEffect(() => {
    if (!isInEditMode) {
      setFocussedIndex(null);
    }
  }, [isInEditMode]);

  // click outside to exit edit mode
  useEffect(() => {
    if (!isInEditMode) return;

    const currentValues = [...(values || [])];

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest(`#${path.join('-')}`)) {
        setHighlightPath([]);
        setFocussedIndex(null);
        setInputValue('');
        const newValues = editValues;
        if (inputValue.length) {
          newValues.push(inputValue);
        }
        if (newValues.join('') !== currentValues.join('')) {
          handleSave(newValues, false);
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editValues, isInEditMode, inputValue]);

  function handleDelete(e: ReactMouseEvent<HTMLDivElement>, value: string, index: number) {
    if (!onEditSave) return;
    e.stopPropagation();
    const newValues = editValues.filter((v, i) => i !== index && v !== value);
    setFocussedIndex(null);
    handleSave(newValues, !verified);
    inputRef.current?.focus();
  }

  function handleFocus() {
    setFocussedIndex(null);
    setHighlightPath(path);
  }

  // keyhandlers for the container and the badges within the container
  function handleContainerKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!onEditSave) return;
    if (e.key === 'Escape') {
      if (values && values.join('') !== editValues.join('')) {
        handleSave(editValues, true);
      }
      setEditValues(values || []);
      setFocussedIndex(null);
      setHighlightPath([]);
    }
  }

  function handleAddNewValue(newValue: string) {
    if (!onEditSave) return;
    if (newValue) {
      const newValues = [...editValues, newValue];
      handleSave(newValues, !verified);
      setHighlightPath([]);
    }
  }

  function handleSave(newValues: string[], verified: boolean) {
    if (!onEditSave) return;
    const previousValues = values;
    let action: ActualsUpdateType = 'UPDATE';
    if (verified) {
      action = 'VERIFY';
    } else if (previousValues && previousValues.length < newValues.length) {
      action = 'ADD';
    } else if (previousValues && previousValues.length > newValues.length) {
      action = 'REMOVE';
    }
    onEditSave(newValues.length > 0 ? newValues : [], path, action);
  }

  function handleValueChange(newValue: string) {
    if (!onEditSave) return;
    if (focussedIndex !== null) {
      let newValues: string[];
      if (newValue === '') {
        newValues = editValues.filter((v, i) => i !== focussedIndex);
      } else {
        newValues = [...(values ?? [])];
        newValues[focussedIndex] = newValue;
      }
      handleSave(newValues, !verified);
      setFocussedIndex(null);
      setHighlightPath([]);
    }
  }

  const handleEditValue = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    if (!onEditSave) return;
    e.stopPropagation();
    setFocussedIndex(index);
  };

  const handleMarkVerified = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    handleSave(editValues, true);
  };

  return (
    <div
      ref={containerRef}
      autoFocus
      tabIndex={0}
      id={path.join('-')}
      data-placeholder="Enter value"
      suppressContentEditableWarning
      data-readonly={onEditSave ? 'false' : 'true'}
      onClick={onEditSave ? handleFocus : undefined}
      onKeyDown={onEditSave ? handleContainerKeyDown : undefined}
      className={cn(
        'pl-1 min-h-7 max-w-full shrink-0 flex flex-wrap gap-1 relative items-center rounded-sm w-full p-0.5 leading-tight',
        onEditSave && 'group/primitive-entity-value data-[readonly=false]:group-hover/primitive-entity-value:bg-accent',
        focussedIndex !== null || (isInEditMode && 'data-[readonly=false]:bg-accent'),
        onEditSave ? 'cursor-pointer' : 'cursor-text'
      )}
    >
      {!!editValues?.length &&
        editValues.map((value, index) => {
          const isSelected = index === focussedIndex;
          if (isSelected && onEditSave) {
            return (
              <EditField
                autoFocus
                onInput={markLastItemsInRows}
                key={`edit-${index}`}
                value={value}
                onChange={handleValueChange}
                onBlur={() => {
                  setFocussedIndex(null);
                  markLastItemsInRows();
                }}
                onFocus={markLastItemsInRows}
                className="value-item border border-primary mx-0.5 bg-white"
              />
            );
          }
          return (
            <div
              key={`display-${index}`}
              className={cn(
                'value-item group/value-item',
                'relative min-h-6 inline-flex rounded-sm items-stretch cursor-pointer',
                onEditSave ? 'cursor-pointer' : 'cursor-text',
                focussedIndex === null &&
                  onEditSave &&
                  '[&.flex-row-last]:pr-5 [.value-item:not(.flex-row-last):hover~&]:pr-0 hover:[box-shadow:0px_1px_4px_0px_#E5E5E5] hover:bg-accent/50'
              )}
            >
              <div
                className={cn(
                  'inline-flex items-center cursor-pointer rounded-sm',
                  onEditSave &&
                    '[&.flex-row-last:has(div:hover)]:rounded-r-none [.flex-row-last>*&:has(.icon-tray:hover)]:rounded-r-none',
                  onEditSave && 'group-hover/primitive-entity-value:bg-white',
                  isInEditMode && 'bg-white',
                  onEditSave ? 'cursor-pointer' : 'cursor-text'
                )}
              >
                <WithTooltip tooltip="Edit" delay={500} disabled={!onEditSave || focussedIndex !== null}>
                  <div
                    className={cn(
                      'leading-5 flex-center break-all min-h-6 px-1 !h-full rounded-l-sm',
                      focussedIndex === null && onEditSave && 'hover:bg-accent'
                    )}
                    onClick={(e) => handleEditValue(e, index)}
                  >
                    {value}
                  </div>
                </WithTooltip>
                {/* Hidden icon tray that expands on hover */}
                <WithTooltip tooltip="Delete" delay={500}>
                  <div
                    onClick={(e) => handleDelete(e, value, index)}
                    aria-label="Delete value"
                    className={cn(
                      'icon-tray',
                      'flex-center hidden overflow-hidden bg-white w-5 shrink-0 rounded-r-sm h-full z-10 text-typography-darkgrey hover:text-destructive',
                      focussedIndex === null &&
                        onEditSave &&
                        'hover:bg-accent group-hover/value-item:flex [.flex-row-last>*>&]:absolute [.flex-row-last>*>&]:top-0 [.flex-row-last>*>&]:right-0'
                    )}
                  >
                    <X size={14} />
                  </div>
                </WithTooltip>
              </div>
            </div>
          );
        })}
      {onEditSave && (isInEditMode || values?.length) ? (
        // Treat the EditField as a value-item for correct rendering
        <EditField
          ref={editFieldRef}
          placeholder="Add a value"
          autoFocus
          onFocus={() => setHighlightPath(path)}
          onChange={handleAddNewValue}
          onInput={markLastItemsInRows}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && e.currentTarget.textContent === '') {
              setFocussedIndex(editValues.length - 1);
            }
          }}
          onBlur={markLastItemsInRows}
          className={cn(
            'value-item flex-row-last outline-none rounded-sm hidden placeholder:text-typography-darkgrey bg-neutral-100 min-w-20 min-h-5 leading-6',
            'group-hover/primitive-entity-value:inline focus:inline focus:bg-accent',
            !values?.length && 'focus:border focus:border-primary focus:bg-white basis-full',
            !verified && 'group-hover/primitive-entity-value:mr-8'
          )}
        />
      ) : !values?.length ? (
        <div className="value-item ml-1 text-typography-darkgrey basis-full min-h-6 flex items-center justify-start">
          <span className={onEditSave ? 'group-hover/primitive-entity-value:hidden' : ''}>Empty</span>
          {onEditSave && <span className="hidden group-hover/primitive-entity-value:inline">Add a value</span>}
        </div>
      ) : null}
      {onEditSave && (!verified || values?.join('') !== editValues.join('')) && (
        <div className="hidden absolute -right-0.5 -bottom-1 w-fit px-1 py-1.5 group-hover/primitive-entity-value:block">
          <WithTooltip align="end" tooltip="Mark as correct" delay={500}>
            <div className="flex items-center rounded-sm bg-muted shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">
              <div
                onClick={(e) => handleMarkVerified(e)}
                className="flex-center h-6 w-6 border border-border rounded-sm hover:bg-success-600/20 hover:text-success hover:border-success-600/20"
              >
                <CheckCheck size={14} />
              </div>
            </div>
          </WithTooltip>
        </div>
      )}
    </div>
  );
}
